import { Card, Divider, Pagination, Button, message } from 'antd';
import { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams, useParams } from 'react-router-dom';
import moment from 'moment';
import { CSVLink } from 'react-csv';
import styled from 'styled-components';
import { DownloadOutlined } from '@ant-design/icons';
import SearchFilter from '../../component/Filter';
import baseuri from '../../methods/baseuri';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import {
	getBlockchainNetworks,
	getCurrencies,
} from '../../methods/redux/reducers/currency';
import { getUserProfileById } from '../../methods/redux/reducers/user-profile';
import {
	getWithdrawalRequestCount,
	getWithdrawalRequests,
} from '../../methods/redux/reducers/withdrawal-requests';
import remakeQueryString from '../../methods/utils/remake-query-string';
import StatusCount from './StatusCount';
import WithdrawalRequestItem from './WithdrawalRequestItem';

const DownloadButton = styled(Button)`
	color: red;
	background-color: #f5d5d5;
	border-radius: 5px;
	&:hover {
		color: red;
		background-color: #efe7e7;
	}
	&:focus {
		color: red;
		background-color: #f5d5d5;
	}
`;

const interpretStatus = (statusValue: any) => {
	switch (statusValue) {
		case '0':
			return 'Pending';
		case '1':
			return 'Completed';
		case '2':
			return 'Failed';
		case '3':
			return 'Cancelled';
		case '4':
			return 'Deleted';
		case '5':
			return 'Processing';
		case '6':
			return 'Retry';
		case '7':
			return 'On-Hold';
		case '8':
			return 'Review';
		default:
			return 'Unknown';
	}
};

const WithdrawalRequests = () => {
	const dispatch = useAppDispatch();

	const { userId } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const [loading, setLoading] = useState(false);
	const [results, setResults] = useState([] as any);

	const getQueryParams = () => Object.fromEntries(searchParams.entries());

	const { itemsList, paginator } = useAppSelector(
		(store) => store.withdrawalRequests
	);
	const { user } = useAppSelector((store) => store.userProfile);
	const { currencies, blockchains } = useAppSelector(
		(store) => store.currencies
	);

	const csvLinkRef = useRef<
		CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
	>(null);

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length === 0) {
			setSearchParams({
				status: '8',
				pageId: '1',
				perPage: String(20),
			});
		}

		if (userId) {
			dispatch(getUserProfileById(userId));
			dispatch(getWithdrawalRequestCount({ userId }));
		}

		dispatch(getWithdrawalRequestCount({}));
		dispatch(getCurrencies({}));
		dispatch(getBlockchainNetworks());
	}, []);

	const updateQueryString = (query: any, action = 'update') => {
		remakeQueryString(query, searchParams, setSearchParams, action);
	};

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length > 0) {
			if (userId) {
				dispatch(getWithdrawalRequests({ ...queryData, userId }));
			} else {
				dispatch(getWithdrawalRequests(queryData));
			}
		}
	}, [searchParams]);

	const currencySelect = currencies.map((item: any) => ({
		name: item.symbol,
		value: item._id,
	}));

	const blockchainSelect = blockchains.map((item: any) => ({
		name: item.abbreviation,
		value: item._id,
	}));

	// Transform the results data to match the headers
	const transformSwapRequests = (requests: any[]) =>
		requests?.map((data: any) => ({
			username: data?.userId?.username,
			amount: `${data?.finalAmount} (${data?.currencyId?.symbol})`,
			withdrawalType: data?.withdrawalType,
			status: interpretStatus(String(data?.status)),
			destination: data?.destinationAddress
				? `https://${data?.blockchainNetworkId?.explorerUrl}/address/${data?.destinationAddress}#tokentxns`
				: '--',
			isWhitelisted: `${
				data?.withdrawalAddressWhiteListId ? 'whitelisted' : 'not whitelisted'
			}`,
			blockchain: data?.blockchainNetworkId?.abbreviation,
			transactionHash: data?.transactionHash
				? `https://${data?.blockchainNetworkId?.explorerUrl}/tx/${data?.transactionHash}`
				: '--',
			fee: `${data?.fee} ${data?.currencyId?.symbol}`,
			p2pRef: data?.p2pRef,
			blockchainMemo: data?.blockchainMemo,
			processedBy: data?.processedBy?.email,
			lastStatusChanged: data?.lastStatusChangedByUserId?.email,
			inAppTransaction: `${data?.inAppTransaction ? 'True' : 'False'}`,
			formerBalance: `${data.formerBalance} ${data.currencyId?.symbol}`,
			paymentMethod: `${
				data?.destinationPaymentDetailId?.paymentMethod ?? '--'
			}`,
			bankName: `${data?.destinationPaymentDetailId?.bankName ?? '--'}`,
			accountName: `${data?.destinationPaymentDetailId?.accountName ?? '--'}`,
			accountNumber: `${
				data?.destinationPaymentDetailId?.accountNumber ?? '--'
			}`,
			sortCode: `${data?.destinationPaymentDetailId?.sortCode ?? '--'}`,

			fiatPaymentAccountName: `${
				data?.fiatPaymentAccountDetails?.accountName ?? '--'
			}`,
			fiatPaymentAccountBankCode: `${
				data?.fiatPaymentAccountDetails?.bankCode ?? '--'
			}`,
			fiatPaymentAccountBvn: `${data?.fiatPaymentAccountDetails?.bvn ?? '--'}`,
			fiatPaymentAccountKycLevel: `${
				data?.fiatPaymentAccountDetails?.kycLevel ?? '--'
			}`,
			fiatPaymentAccountMessage: `${
				data?.fiatPaymentAccountDetails?.message ?? '--'
			}`,

			createdAt: moment(data?.createdAt).format('lll'), // Handle computed value
			updatedAt: moment(data?.updatedAt).format('lll'),
		}));

	// Define headers with simple keys
	const headers = [
		{ label: 'username', key: 'username' },
		{ label: 'Amount', key: 'amount' },
		{ label: 'Withdrawal Type', key: 'withdrawalType' },
		{ label: 'Status', key: 'status' },
		{ label: 'Destination', key: 'destination' },
		{ label: 'Destination Whitelisted', key: 'isWhitelisted' },
		{ label: 'blockchain', key: 'blockchain' },
		{ label: 'Transaction Hash', key: 'transactionHash' },
		{ label: 'Transaction fee', key: 'fee' },
		{ label: 'XendBridge Ref.', key: 'p2pRef' },
		{ label: 'BlockChain Memo', key: 'blockchainMemo' },
		{ label: 'Processed By', key: 'processedBy' },
		{ label: 'Last Status Change', key: 'lastStatusChanged' },
		{ label: 'InApp Transaction', key: 'inAppTransaction' },
		{ label: 'Prev. Balance', key: 'formerBalance' },
		{ label: 'Payment Method', key: 'paymentMethod' },
		{ label: 'InApp Bank Name', key: 'bankName' },
		{ label: 'Account Name', key: 'accountName' },
		{ label: 'Account Number', key: 'accountNumber' },
		{ label: 'Sort Code', key: 'sortCode' },
		{ label: 'Fiat Account Name', key: 'fiatPaymentAccountName' },
		{ label: 'Fiat Bank Code', key: 'fiatPaymentAccountBankCode' },
		{ label: 'Fiat Account Bvn', key: 'fiatPaymentAccountBvn' },
		{ label: 'Fiat Account KYC', key: 'fiatPaymentAccountKycLevel' },
		{ label: 'Fiat Account Message', key: 'fiatPaymentAccountMessage' },

		{ label: 'Created At', key: 'createdAt' },
		{ label: 'Updated At', key: 'updatedAt' },
	];

	const handleDownload = () => {
		setLoading(true);
		const params1 = getQueryParams();
		setResults([]);
		baseuri
			.get('/admin/wallet/withdrawal-requests' || '', {
				params: {
					...params1,
					perPage: paginator.itemCount || 0,
					pageId: '1',
				},
			})
			.then((res: any) => {
				const rawData = res.data.data.itemsList;
				const transformedData = transformSwapRequests(rawData);
				setResults(transformedData);
				setLoading(false);
			})
			.catch(() => {
				message.error('Could not download CSV');
				setLoading(false);
			});
	};

	useEffect(() => {
		if (results.length > 0) {
			csvLinkRef?.current?.link.click();
		}
	}, [results]);

	return (
		<Card>
			<h1>
				Withdrawal Requests{' '}
				{userId && (
					<span>
						for{' '}
						<Link
							title="View Profile"
							to={`/user-profile/${user.profile?._id}`}
						>
							@{user.profile?.username}
						</Link>
					</span>
				)}
			</h1>
			<Divider />
			<StatusCount />

			<br />
			<br />
			<SearchFilter
				title="Filter Withdrawal Requests"
				searchFilters={[
					{
						label: 'Status',
						name: 'status',
						htmlElement: 'select',
						defaultSelectedValue: '8',
						selectOptions: [
							{ name: 'Review', value: '8' },
							{ name: 'Pending', value: '0' },
							{ name: 'On-Hold', value: '7' },
							{ name: 'Processing', value: '5' },
							{ name: 'Completed', value: '1' },
							{ name: 'Cancelled', value: '3' },
							{ name: 'Retry', value: '6' },
							{ name: 'Failed', value: '2' },
							{ name: 'Refunded', value: '9' },
							{ name: 'Deleted', value: '4' },
						],
					},
					{
						label: 'Currency',
						name: 'currencyId',
						htmlElement: 'select',
						selectOptions: currencySelect,
					},
					{
						label: 'Blockchain Network',
						name: 'blockchainNetworkId',
						htmlElement: 'select',
						selectOptions: blockchainSelect,
					},
					{
						label: 'XendBridge Reference',
						name: 'p2pRef',
						htmlElement: 'input',
					},
					{
						label: 'Destination Address',
						name: 'destinationAddress',
						htmlElement: 'input',
					},
					{
						label: 'Transaction hash',
						name: 'transactionHash',
						htmlElement: 'input',
					},
					{
						label: 'Start Date',
						name: 'startDate',
						htmlElement: 'datepicker',
					},
					{
						label: 'End Date',
						name: 'endDate',
						htmlElement: 'datepicker',
					},
				]}
				retrieveQueryString={updateQueryString}
			/>
			<br />
			<Pagination
				onChange={(pageId) => updateQueryString({ pageId })}
				total={paginator.itemCount}
				current={paginator.currentPage}
				pageSize={paginator.perPage}
			/>
			{itemsList.length > 0 && (
				<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
					<DownloadButton
						danger
						loading={loading}
						icon={<DownloadOutlined />}
						onClick={() => handleDownload()}
					>
						{loading ? 'Loading csv...' : 'Download CSV'}
					</DownloadButton>

					<CSVLink
						data={results}
						headers={headers}
						className="download"
						filename="Withdrawal Requests.csv"
						ref={csvLinkRef}
					/>
				</div>
			)}
			<br />
			{itemsList.map((item: any) => (
				<WithdrawalRequestItem key={item._id} request={item} />
			))}
			<Divider />
			<Pagination
				onChange={(pageId) => updateQueryString({ pageId })}
				total={paginator.itemCount}
				current={paginator.currentPage}
				pageSize={paginator.perPage}
				showSizeChanger
				showQuickJumper
			/>
		</Card>
	);
};

export default WithdrawalRequests;
